/**
 * Persisted scope + filter state for the search-first /documents finder.
 *
 * Owns the in-memory state (scope picked via the omni-scope picker + the
 * orthogonal Source / Type / Date / Sort filters + the hero filename search)
 * and round-trips it through localStorage["njbsoft.documents.filters"]. The page
 * separately mirrors the same state into the URL search params; on mount the URL
 * wins (passed in as `initialOverride`), else localStorage, else defaults.
 *
 * Mirrors the task-list filter-persistence precedent (use-task-filters): a
 * single version-stamped blob, malformed/version-mismatch JSON falls back to
 * defaults, and writes are debounced so typing doesn't thrash localStorage.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { type DocumentSource, type DocumentSortBy } from "@/hooks/use-document-repo";
import { type DocumentScope, type ScopeChange } from "@/components/documents/document-scope-picker";

/** The complete persisted finder state. Scope is the picked entity axis (null
 *  = org-wide); the rest are orthogonal filters that combine with the scope. */
export interface DocumentFiltersState {
  scope: DocumentScope | null;
  /** Hero filename search (the org-wide filename substring). */
  q?: string;
  source?: DocumentSource[];
  type?: string;
  created_after?: string;
  created_before?: string;
  sortBy: DocumentSortBy;
  sortDir: "asc" | "desc";
}

/** Bump when the persisted shape changes incompatibly. */
export const DOCUMENT_FILTERS_VERSION = 1;
export const DOCUMENT_FILTERS_KEY = "njbsoft.documents.filters";
const DEBOUNCE_MS = 300;

export function defaultDocumentFilters(): DocumentFiltersState {
  return { scope: null, sortBy: "created_at", sortDir: "desc" };
}

interface PersistedShape extends DocumentFiltersState {
  version: number;
  /** The org the scope was picked under; a stale cross-org scope is dropped. */
  orgId: string;
}

/**
 * Load the persisted finder state for `orgId`, or null if absent/invalid. The
 * scope carries org-specific node ids, so a scope persisted under a different
 * org is dropped (the org-agnostic filters — source/type/date/sort/q — survive).
 */
export function loadDocumentFilters(orgId: string): DocumentFiltersState | null {
  try {
    const raw = localStorage.getItem(DOCUMENT_FILTERS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      (parsed as Record<string, unknown>).version !== DOCUMENT_FILTERS_VERSION
    ) {
      return null;
    }
    const { version: _version, orgId: storedOrg, ...state } = parsed as PersistedShape;
    const next: DocumentFiltersState = { ...defaultDocumentFilters(), ...state };
    if (storedOrg !== orgId) next.scope = null;
    return next;
  } catch {
    return null;
  }
}

export interface UseDocumentFiltersReturn {
  state: DocumentFiltersState;
  /** Shallow-merge a partial into the state. */
  patch: (partial: Partial<DocumentFiltersState>) => void;
  /** Apply the scope picker's discriminated result. */
  applyScopeChange: (change: ScopeChange) => void;
  /** Reset scope + every filter + sort back to defaults. */
  resetAll: () => void;
}

export function useDocumentFilters(
  orgId: string,
  initialOverride: DocumentFiltersState | null,
): UseDocumentFiltersReturn {
  const [state, setState] = useState<DocumentFiltersState>(
    () => initialOverride ?? loadDocumentFilters(orgId) ?? defaultDocumentFilters(),
  );

  // On a REAL org switch the scope (org-specific node id) is dropped; org-
  // agnostic filters are kept. The initial falsy→real resolve (store hydrating)
  // is NOT a switch, so it must not wipe a URL/localStorage-restored scope.
  const prevOrgRef = useRef(orgId);
  useEffect(() => {
    const prev = prevOrgRef.current;
    if (orgId !== prev) {
      prevOrgRef.current = orgId;
      if (prev) setState((s) => ({ ...s, scope: null }));
    }
  }, [orgId]);

  // Debounced persist after every state change.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const persisted: PersistedShape = { ...state, version: DOCUMENT_FILTERS_VERSION, orgId };
      try {
        localStorage.setItem(DOCUMENT_FILTERS_KEY, JSON.stringify(persisted));
      } catch {
        // ignore quota / unavailable storage
      }
      debounceRef.current = null;
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current !== null) clearTimeout(debounceRef.current);
    };
  }, [state, orgId]);

  const patch = useCallback(
    (partial: Partial<DocumentFiltersState>) => setState((s) => ({ ...s, ...partial })),
    [],
  );

  const applyScopeChange = useCallback((change: ScopeChange) => {
    setState((s) => {
      // entity → set the node axis; source → set the source filter + clear the
      // node axis; all → clear the scope (org-wide).
      if (change.kind === "entity") return { ...s, scope: change.scope };
      if (change.kind === "source") return { ...s, scope: null, source: [change.source] };
      return { ...s, scope: null };
    });
  }, []);

  const resetAll = useCallback(() => setState(defaultDocumentFilters()), []);

  return { state, patch, applyScopeChange, resetAll };
}
